<template>
  <section class="announce-modal">
    <header class="announce-header">
      <template v-if="!showSaveButton">
        <VPButton
          theme="base-dark"
          variant="text"
          size="xs"
          class="justify-start"
          @click.prevent="close"
        >
          Close
        </VPButton>
      </template>
      <template v-else>
        <VPButton
          variant="text"
          class="ml-auto"
          size="xs"
          @click.prevent="close"
        >
          Cancel
        </VPButton>
      </template>
    </header>
    <main
      v-if="selectedEvent"
      class="announce-content"
    >
      <!-- <h2 class="vp-dark font-bold text-4xl mt-5"> -->
      <!-- NOTE: If event name, title is uncommented remove `mt-5` class -->
      <!-- {{ selectedEvent && selectedEvent.name }} -->
      <!-- </h2> -->
      <!-- <ComponentsTemplate /> -->

      <Form
        :form-id="formId"
        :initial-values="selectedEvent?.announceDetail"
        class="announce-form"
        @submit.prevent="() => {}"
      >
        <BoxedFrame
          :title="eventStartTime"
          class="shrink pb-8"
        >
          <HiddenInput
            input-id="eventId"
            label="Event ID"
            type="hidden"
            :value="selectedEvent?.id"
          />

          <div class="event-name-group">
            <VPInput
              label="Event Billing"
              class="vp-event-name"
              input-id="eventName"
            />
            <Badge
              :content="statusBadge.text"
              :class="statusBadge.class"
            />
          </div>

          <div class="event-name-group">
            <VPInput
              label="Subtitle"
              class="promoter"
              input-id="support"
              placeholder="Add subtitle"
            />
          </div>

          <div class="event-promotor-announce-group">
            <VPInput
              class="promoter"
              input-id="promoter"
              label="Promoter"
              placeholder="Add promoter"
            />

            <VPDateTimeInput
              class="announce-at"
              input-id="announceAt"
              :label="datePickerLabel"
              placeholder="Add Date"
              time-placeholder="Add time"
              clearable
              text-input
              :timezone="selectedEvent.timezone"
              :with-time="true"
              :hide-time="!formsStore.fieldGetValue(formId, 'announceAt')"
              use-native-date-time-pickers
            />
          </div>
          <hr class="my-3 bg-base-200">
          <ArtistSection
            :artists="artists"
            @add="addArtistRow"
            @remove="removeArtistRow"
          />
          <hr class="mt-4 mb-3 bg-base-200">
          <ImageSection
            :attachments="attachments"
            @update:ready-attachments="updateAttachments"
          />
          <hr class="my-3 bg-base-200">

          <VPRichText
            input-id="description"
            label="Description"
            placeholder="Add description text"
          />

          <hr class="my-3 bg-base-200">

          <div class="py-2">
            <!-- <VPTokenInput label="Tags" input-id="tags" multiple input-placeholder="Add tag" :separator="[',']" /> -->
            <VPInput
              input-id="tags"
              label="Tags (separated by comma)"
              placeholder="Add tags"
            />
          </div>
          <hr class="my-3 bg-base-200">

          <div class="py-2">
            <div class="inline-block w-4/12 relative">
              <VPDropdown
                input-id="ticketsUrlMode"
                label="Ticket Link"
                :options="ticketLinkMode"
                @change="onTicketLinkModeChange"
              />
            </div>

            <div class="inline-block w-8/12">
              <VPInput
                input-id="ticketsUrl"
                label="URL"
                :placeholder="ticketsUrlPlaceholder"
                :disabled="isNotUsingExternalLink"
                @change="onTicketUrlChange"
              />
            </div>
          </div>

          <hr class="my-3 bg-base-200">

          <div class="py-2 relative has-value">
            <VPDropdown
              input-id="buttonText"
              label="Ticket Sale Button Text"
              :options="buttonTextOptionsComputed"
            />

            <VPInput
              v-if="isButtonTextCustom"
              class="mt-2"
              input-id="buttonTextAdditional"
              placeholder="Add button text"
            />
          </div>

          <div class="my-3 bg-base-200">
            <VPLabel :label="$gettext('Tracking')" />
            <div>
              <div
                v-for="trackingItem in tracking"
                :key="trackingItem.formId"
                class="my-3"
              >
                <Form
                  v-show="!trackingItem.entry._destroy"
                  :form-id="trackingItem.formId"
                  :initial-values="trackingItem.entry"
                  class="tracking-form flex flex-col sm:flex-row gap-2 relative"
                >
                  <HiddenInput input-id="id" />
                  <HiddenInput input-id="_destroy" />

                  <div class="min-w-full sm:min-w-48">
                    <TrackingProviderSelect
                      input-id="provider"
                      :placeholder="$gettext('Select Provider')"
                    />
                  </div>
                  <div class="flex flex-row gap-4">
                    <VPInput
                      input-id="trackingId"
                      :placeholder="$gettext('Enter tracking id')"
                    />
                    <button
                      type="button"
                      @click.prevent="removeTrackingRow(trackingItem.listId)"
                    >
                      <iconify-icon
                        icon="mdi:trash-can-outline"
                        class="text-vp-pink text-xl"
                      />
                    </button>
                  </div>
                </Form>
              </div>

              <VPButton
                class="mt-3 pl-4 pr-4"
                size="md"
                height="2rem"
                @click="addTrackingRow"
              >
                {{ $gettext('Add Tracking') }}
              </VPButton>
            </div>
          </div>
        </BoxedFrame>
        <div
          class="announce-footer"
          :class="{ invisible: !showSaveButton }"
        >
          <VPButton
            :disabled="isSaveBtnDisabled"
            :label="saveButtonLabel"
            type="submit"
            size="lg"
            theme="primary"
            class="min-w-full py-4"
            @click.prevent.stop="onSubmit"
          />
        </div>
      </Form>
    </main>
  </section>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router';
import {
  ButtonTextEnum,
  TicketLinkModeEnum,
  type SaveAnnounceParams,
  useEventStore,
  type TrackingEntry,
} from '@/stores/event';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { DateTime } from 'luxon';
import BoxedFrame from '@/components/BoxedFrame.vue';
import Badge from '@/components/Badge.vue';
import { Form, HiddenInput, useFormsStore } from 'vuniform';
import VPInput from '@/components/form/VPInput.vue';
import VPButton from '@/components/form/VPButton.vue';
import VPRichText from '@/components/form/VPRichText.vue';
import VPDropdown from '@/components/form/VPDropdown.vue';
import ImageSection from '@/components/announce-modal/ImageSection.vue';
import ArtistSection from '@/components/announce-modal/ArtistSection.vue';
import type { AnnounceArtist, AnnounceAttachment, AnnounceAttachmentInput } from '@/generated/graphql';
import VPDateTimeInput from '@/components/form/VPDateTimeInput.vue';
import VPLabel from '@/components/form/VPLabel.vue';
import TrackingProviderSelect from '@/components/announce-modal/TrackingProviderSelect.vue';
import { useToast } from 'vue-toastification';
import { useGettext } from 'vue3-gettext';

const router = useRouter();
const eventStore = useEventStore();
const formsStore = useFormsStore();

const formId = 'announce-form';
const close = () => router.push('/');
const selectedEvent = computed(() => eventStore.selectedEvent);
const toast = useToast();
const { $gettext } = useGettext();

const localCustomUrl = ref<string | null>(null);

onMounted(async () => {
  await eventStore.fetchSelectedEvent();
  formsStore.STAGE_FIELD_CHANGE({
    value: selectedEvent.value?.id,
    query: { formId: formId, field: 'eventId' },
  });
  formsStore.STAGE_FIELD_CHANGE({
    value: selectedEvent.value?.name,
    query: { formId: formId, field: 'eventName' },
  });

  const btnText = selectedEvent.value?.announceDetail?.buttonText;
  if (btnText && Object.values(ButtonTextEnum).includes(btnText as ButtonTextEnum)) {
    const buttonTextValue = selectedEvent.value?.announceDetail?.buttonText;
    formsStore.STAGE_FIELD_CHANGE({
      value: buttonTextValue,
      query: { formId: formId, field: 'buttonText' },
    });
  } else {
    formsStore.STAGE_FIELD_CHANGE({
      value: ButtonTextEnum.TICKETS,
      query: { formId: formId, field: 'buttonText' },
    });
    const values = { buttonTextAdditional: btnText };
    formsStore.SET_INITIAL_VALUES({ values: values, formId });
  }

  loadAttachments();
  loadArtists();
  loadTracking();

  if (selectedEvent.value?.announceDetail?.ticketsUrlMode === TicketLinkModeEnum.USE_EXTERNAL_LINK) {
    localCustomUrl.value = selectedEvent.value?.announceDetail?.ticketsUrl;
  }

  formsStore.CLEAR_FORM_DIRTY(formId);
});

onUnmounted(() => {
  formsStore.DEINIT_FORM(formId);
  eventStore.$patch({ selectedEvent: undefined });
});

const loadAttachments = () => {
  if (selectedEvent.value?.announceDetail?.attachments) {
    attachments.value = selectedEvent.value?.announceDetail?.attachments
      .slice()
      .sort((a, b) => (+a.id > +b.id ? 1 : -1));
  }
};

const loadArtists = () =>
  (artists.value = (selectedEvent?.value?.announceDetail?.announceArtists ?? []).map((artist, index) => ({
    listId: index,
    formId: calculateArtistFormId(index),
    artist,
  })));

const loadTracking = () => {
  if (selectedEvent.value?.announceDetail?.tracking) {
    tracking.value = selectedEvent.value?.announceDetail?.tracking.map((entry, index) => ({
      listId: index,
      formId: `tracking-${index}`,
      entry: {
        ...entry,
        _destroy: false,
      },
    }));
  }
};

const setArtistsFields = () => {
  artists.value.forEach(artist => {
    formsStore.STAGE_FIELD_CHANGE({
      value: artist.artist.id,
      query: { formId: artist.formId, field: 'id' },
    });
    formsStore.STAGE_FIELD_CHANGE({
      value: artist.artist.name,
      query: { formId: artist.formId, field: 'name' },
    });
    formsStore.STAGE_FIELD_CHANGE({
      value: artist.artist.website,
      query: { formId: artist.formId, field: 'website' },
    });
    formsStore.STAGE_FIELD_CHANGE({
      value: artist.artist.instagram,
      query: { formId: artist.formId, field: 'instagram' },
    });
    formsStore.STAGE_FIELD_CHANGE({
      value: artist.artist.youtube,
      query: { formId: artist.formId, field: 'youtube' },
    });
    formsStore.STAGE_FIELD_CHANGE({
      value: artist.artist.spotify,
      query: { formId: artist.formId, field: 'spotify' },
    });
    formsStore.STAGE_FIELD_CHANGE({
      value: artist.artist.bandcamp,
      query: { formId: artist.formId, field: 'bandcamp' },
    });
    formsStore.STAGE_FIELD_CHANGE({
      value: artist.artist.facebook,
      query: { formId: artist.formId, field: 'facebook' },
    });
    formsStore.STAGE_FIELD_CHANGE({
      value: artist.artist.twitter,
      query: { formId: artist.formId, field: 'twitter' },
    });
  });
};

const setTrackingFields = () => {
  tracking.value.forEach(trackingItem => {
    formsStore.STAGE_FIELD_CHANGE({
      value: trackingItem.entry.id,
      query: { formId: trackingItem.formId, field: 'id' },
    });
    formsStore.STAGE_FIELD_CHANGE({
      value: trackingItem.entry.provider,
      query: { formId: trackingItem.formId, field: 'provider' },
    });
    formsStore.STAGE_FIELD_CHANGE({
      value: trackingItem.entry.trackingId,
      query: { formId: trackingItem.formId, field: 'trackingId' },
    });
  });
};

const formIsDirty = computed(
  () =>
    formsStore.isFormDirty(formId) ||
    artists.value.some(artist => formsStore.isFormDirty(artist.formId)) ||
    tracking.value.some(trackingItem => formsStore.isFormDirty(trackingItem.formId)) ||
    artistRemoved.value ||
    attachmentsChanged.value,
);

const eventStartTime = computed(() => {
  if (!selectedEvent.value) {
    return '';
  }
  return DateTime.fromISO(selectedEvent.value.startTime)
    .setZone(selectedEvent.value.timezone)
    .toLocaleString(DateTime.DATETIME_MED);
});

const statusBadge = computed(() => {
  const announceAt = selectedEvent.value?.announceDetail?.announceAt;

  if (!announceAt || !selectedEvent.value?.startTime) {
    return {
      text: 'Unscheduled',
      class: 'unscheduled',
    };
  }
  const now = DateTime.local().setZone(selectedEvent.value.timezone);
  if (announceAt && now >= DateTime.fromISO(announceAt).endOf('minute')) {
    return {
      text: 'Live',
      class: 'live',
    };
  }

  if (announceAt) {
    return {
      text: 'Scheduled',
      class: 'scheduled',
    };
  }

  return {
    text: 'Unscheduled',
    class: 'unscheduled',
  };
});

const datePickerLabel = computed(() => {
  const time = DateTime.now();
  const dateFormatExample = time.toFormat('LLL dd, yyyy');
  const timeFormatExample = time.toFormat('h:mm a');

  return `Start Announce (eg: "${dateFormatExample}" and "${timeFormatExample}")`;
});

const attachmentInputs = ref<AnnounceAttachmentInput[]>([]);
const attachments = ref<AnnounceAttachment[]>([]);
const artists = ref<{ listId: number; formId: string; artist: AnnounceArtist }[]>([]);
const artistRemoved = ref<boolean>(false);
const tracking = ref<{ listId: number; formId: string; entry: TrackingEntry }[]>([]);
const attachmentsChanged = ref<boolean>(false);

const updateAttachments = async (readyAttachments: AnnounceAttachment[], formDirty: boolean) => {
  attachmentInputs.value = readyAttachments;

  attachmentsChanged.value = formDirty;
};

const onSubmit = async () => {
  const announceParams: SaveAnnounceParams & { buttonTextAdditional?: string; ticketsUrlMode?: string } =
    formsStore.formGetValues<SaveAnnounceParams>(formId);
  announceParams.attachments = attachmentInputs.value;

  announceParams.announceArtists = artists.value.map(artist => formsStore.formGetValues<AnnounceArtist>(artist.formId));
  announceParams.tracking = tracking.value
    .map(trackingItem => formsStore.formGetValues<TrackingEntry>(trackingItem.formId))
    .filter(({ provider, trackingId }) => provider && trackingId)
  ;

  if (isButtonTextCustom.value) {
    announceParams.buttonText = announceParams.buttonTextAdditional;
  }
  announceParams.buttonTextAdditional = undefined;
  announceParams.ticketsUrlMode = undefined;

  const { errors } = await eventStore.saveAnnounceDetail({ requesterId: formId, params: announceParams });

  if (errors) {
    formsStore.SET_ERRORS({ requesterId: formId, errors });
    toast.error($gettext('Failed to save announce details'));
    return;
  }

  toast.success($gettext('Announce details saved successfully!'));

  attachments.value = selectedEvent.value?.announceDetail?.attachments.slice().sort((a, b) => (+a.id > +b.id ? 1 : -1));

  loadArtists();
  setArtistsFields();
  loadTracking();
  setTrackingFields();
  formsStore.CLEAR_FORM_DIRTY(formId);
  artists.value.forEach(artist => formsStore.CLEAR_FORM_DIRTY(artist.formId));
  tracking.value.forEach(trackingItem => formsStore.CLEAR_FORM_DIRTY(trackingItem.formId));
  artistRemoved.value = false;
  attachmentsChanged.value = false;
};

const showSaveButton = computed(() => formIsDirty.value);

const isSaveBtnDisabled = computed(() => {
  return !formIsDirty.value;
});

const saveButtonLabel = computed(() => (selectedEvent.value?.announceDetail?.id ? 'Update' : 'Save'));

const buttonTextOptionsComputed = computed(() => {
  return (Object.keys(ButtonTextEnum) as (keyof typeof ButtonTextEnum)[]).map(x => {
    return { value: ButtonTextEnum[x], name: ButtonTextEnum[x] };
  });
});

const isButtonTextCustom = computed(() => {
  return formsStore.fieldGetValue(formId, 'buttonText') === ButtonTextEnum.CUSTOM;
});

const ticketLinkMode = computed(() => {
  return (Object.keys(TicketLinkModeEnum) as (keyof typeof TicketLinkModeEnum)[]).map(x => {
    return { value: TicketLinkModeEnum[x], name: TicketLinkModeEnum[x] };
  });
});

const onTicketUrlChange = event => {
  localCustomUrl.value = event.target.value;
};

const onTicketLinkModeChange = (event: string) => {
  let newValue;

  switch (event) {
    case TicketLinkModeEnum.USE_VP_LINK:
      newValue = selectedEvent.value?.announceDetail?.defaultTicketsUrl;
      break;
    case TicketLinkModeEnum.USE_EXTERNAL_LINK:
      newValue = localCustomUrl.value;
      break;
    default:
      newValue = null;
  }

  formsStore.STAGE_FIELD_CHANGE({
    value: newValue,
    query: { formId: formId, field: 'ticketsUrl' },
  });
};

const ticketsUrlPlaceholder = computed(() => (isNotUsingExternalLink.value ? '-' : 'https://www.example.com'));

const isNotUsingExternalLink = computed(
  () => formsStore.fieldGetValue(formId, 'ticketsUrlMode') !== TicketLinkModeEnum.USE_EXTERNAL_LINK,
);

const calculateArtistFormId = (listId: number) => `announce-form-artist-${listId}`;

const addArtistRow = () => {
  const newListId = artists.value.length > 0 ? artists.value[artists.value.length - 1].listId + 1 : 0;

  artists.value.push({
    listId: newListId,
    formId: calculateArtistFormId(newListId),
    artist: {
      __typename: 'AnnounceArtist' as const,
      applemusic: null,
      bandcamp: null,
      createdAt: null,
      facebook: null,
      instagram: null,
      lastfm: null,
      name: '',
      songkick: null,
      spotify: null,
      twitter: null,
      updatedAt: null,
      website: null,
      wikipedia: null,
      youtube: null,
    } as AnnounceArtist,
  });
};

const removeArtistRow = (listId: number) => {
  artists.value = artists.value.filter(artist => artist.listId !== listId);

  artistRemoved.value = true;
};

const addTrackingRow = () => {
  const newListId = tracking.value.length > 0 ? tracking.value[tracking.value.length - 1].listId + 1 : 0;
  tracking.value.push({
    listId: newListId,
    formId: `tracking-${newListId}`,
    entry: {
      id: null,
      provider: null,
      trackingId: null,
      _destroy: false,
    } as unknown as TrackingEntry & { _destroy?: boolean },
  });
};

const removeTrackingRow = (listId: number) => {
  const trackingItem = tracking.value.find(trackingItem => trackingItem.listId === listId);

  if (trackingItem) {
    trackingItem.entry._destroy = true;
    formsStore.STAGE_FIELD_CHANGE({
      value: true,
      query: { formId: trackingItem.formId, field: '_destroy' },
    });
  }
};
</script>
